import { createContext, useContext, useEffect, useState } from 'react'
import { IUser } from '../interface/user.interface'
import { loadLocalizationData } from '@/services/localization.service'
import { checkAuthState } from '@/services'
import { useQuery } from 'react-query'
import { useLocation } from 'react-router-dom'
import { useIsAuthenticated, useMsal } from '@azure/msal-react'
import HttpRequest from '@/config/axios.config'

interface IState {
  user: any | undefined
  loading: boolean
  setUser: (user: any) => void
}

const AuthContext = createContext<IState | null>(null)
AuthContext.displayName = 'AuthContext'

function useAuth() {
  return useContext(AuthContext)
}

function AuthProvider(props) {
  const [user, setUser] = useState<IUser>()
  const isAuthenticated = useIsAuthenticated()

  // The mutable `user` object must NOT be in the query key: every setUser() below
  // produces a new object reference, which changes the key, mounts a fresh react-query
  // observer (data=undefined, isLoading=true) and re-runs checkAuthState -> setUser ...
  // a refetch loop (the observed /users/profile x2, /translations x3 per load). Those
  // extra in-flight windows let a transient null/role-less user render, which blanks the
  // permission-gated sidebar items (e.g. "My Orders") on a hard refresh of a heavy page.
  // Key only on isAuthenticated (MSAL/SSO) so this runs once per auth state.
  const { isLoading } = useQuery(
    ['CHECK_AUTH', isAuthenticated],
    async () => {
      const nextUser = await checkAuthState()
      if (nextUser) {
        await loadLocalizationData()
      }
      setUser(nextUser)
      return nextUser ?? null
    },
    {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: false,
      staleTime: Infinity,
    }
  )

  return (
    <AuthContext.Provider
      value={{
        user,
        loading: isLoading,
        setUser,
      }}
      {...props}
    />
  )
}

export { AuthProvider, useAuth }
